import { createStore } from "vuex";
import { clearAllUserCaches } from "@/utils/authCleanup";
import { appEvents } from "@/store/app.events";

export const fetchUserInfo = () => {
    return fetch("/api/user/info", { cache: "no-store" })
}

export const verifyIam = (userInfo, resource) => {
    const role = userInfo?.role;
    const isAdmin = Array.isArray(role)
        ? (role.includes('ADM') || role.includes('ADMIN'))
        : (role === 'ADM' || role === 'ADMIN');
    if (userInfo && isAdmin) {
        return true
    }
    if (userInfo) {
        if (userInfo.iam && userInfo.iam.permissions) {
            const permissions = userInfo.iam.permissions.filter((p) => p.resource.indexOf(resource) > -1)
            return permissions.length > 0;
        } else {
            return false
        }
    } else {
        return false
    }
}

const store = createStore({
    state: {
        userInfo: null
    },
    // Single-flight: evita dispatches 'info' concorrentes (resume + deep link +
    // router guard disparavam 3 fetches simultâneos que se invalidavam).
    _infoPromise: null,
    _infoPromiseSid: null,
    getters: {
        hasAiFeature(state) {
            const features = state.userInfo?.membershipFeatures;
            if (!features) return false;
            if (Array.isArray(features)) {
                return features.includes('ai_feature') || features.some(f => f.name === 'ai_feature' || f.featureName === 'ai_feature');
            }
            return Object.keys(features).includes('ai_feature');
        },
        hasChatAssistantFeature(state) {
            const features = state.userInfo?.membershipFeatures;
            if (!features) return false;
            if (Array.isArray(features)) {
                return features.includes('chat_assistant') || features.some(f => f.name === 'chat_assistant' || f.featureName === 'chat_assistant');
            }
            return Object.keys(features).includes('chat_assistant');
        },
        isFree(state) {
            const tier = state.userInfo?.membershipTier;
            const free = (!tier || tier === 'free_membership' || tier === 'free' || tier === 'free_tier') || 
                         (tier !== 'musician_tier' && tier !== 'admin_tier' && tier !== 'backstage_tier' && tier !== 'custom_tier');
            console.log(`[user.store isFree getter] tier=${tier}, isFree=${free}`);
            return free;
        }
    },
    mutations: {
        INFO(state, user) {
            console.log("[user.store INFO mutation]", user);
            state.userInfo = user
        },
        CLEAR_USER(state) {
            state.userInfo = null;
        },
        LOGOUT(state) {
            state.userInfo = null;
            clearAllUserCaches().catch(() => {});
        }
    },
    actions: {
        info(context, force = false) {
            if (context.state.userInfo && context.state.userInfo.id && !force) {
                return Promise.resolve();
            }
            const currentSid = (typeof localStorage !== 'undefined' ? localStorage.getItem('triad_session_id') : '') || '';
            // Reaproveita fetch em andamento apenas se não for 'force' E a sessão for a mesma
            if (store._infoPromise && !force && store._infoPromiseSid === currentSid) {
                return store._infoPromise;
            }
            store._infoPromiseSid = currentSid;
            store._infoPromise = (async () => {
                try {
                    let result;
                    try {
                        result = await fetchUserInfo();
                    } catch (networkErr) {
                        // Erro de REDE (sem resposta HTTP): usa cache offline.
                        // 401 NUNCA cai aqui — fetch resolve com ok:false.
                        const saved = localStorage.getItem('triad_offline_user');
                        if (saved) {
                            try {
                                const parsed = JSON.parse(saved);
                                if (parsed && (parsed.id || parsed.email || parsed.name)) {
                                    context.commit("INFO", parsed);
                                    return;
                                }
                            } catch (err) {
                                console.warn("Failed to parse cached user:", err);
                            }
                        }
                        context.commit("CLEAR_USER");
                        throw networkErr;
                    }
                    if (result && result.status === 401) {
                        // Sessão inválida/expirada: NÃO usa cache — senão o app
                        // finge que está logado e todas as telas ficam em branco.
                        context.commit("CLEAR_USER");
                        const err = new Error("Session expired");
                        err.code = "UNAUTHORIZED";
                        throw err;
                    }
                    if (result && result.ok) {
                        const data = await result.json();
                        if (!data || (!data.id && !data.email && !data.name)) {
                            throw new Error("Invalid or empty user profile response");
                        }
                        console.log("[user.store fetchUserInfo result]", data);

                        // Detect user switch (e.g. from previous user to new user)
                        const savedUserRaw = localStorage.getItem('triad_offline_user');
                        if (savedUserRaw) {
                            try {
                                const savedUser = JSON.parse(savedUserRaw);
                                const prevId = String(savedUser?.id || savedUser?._id || '');
                                const newId = String(data?.id || data?._id || '');
                                const prevEmail = (savedUser?.email || '').toLowerCase().trim();
                                const newEmail = (data?.email || '').toLowerCase().trim();
                                if ((prevId && newId && prevId !== newId) || (prevEmail && newEmail && prevEmail !== newEmail)) {
                                    console.warn(`[user.store] User switch detected (${prevEmail || prevId} -> ${newEmail || newId}). Purging stale user caches.`);
                                    await clearAllUserCaches();
                                    appEvents.emit('playlist:refresh-list');
                                }
                            } catch (e) {
                                console.warn('[user.store] Error checking user switch:', e);
                            }
                        }

                        localStorage.setItem('triad_offline_user', JSON.stringify(data));
                        context.commit("INFO", data);

                        // Auto-detect & silently persist timezone if not set or defaulted to UTC
                        const localTz = typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : null;
                        if (localTz && (!data.timezone || data.timezone === 'UTC' || data.timezone === '')) {
                            try {
                                const syncRes = await fetch('/api/user/profile', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ timezone: localTz })
                                });
                                if (syncRes.ok) {
                                    data.timezone = localTz;
                                    context.commit("INFO", { ...data });
                                    localStorage.setItem('triad_offline_user', JSON.stringify(data));
                                }
                            } catch (tzErr) {
                                console.warn('Could not auto-sync browser timezone:', tzErr);
                            }
                        }
                    } else {
                        context.commit("CLEAR_USER");
                        const err = new Error("Invalid response: HTTP " + (result ? result.status : "unknown"));
                        err.code = (result && result.status === 401) ? "UNAUTHORIZED" : "FETCH_FAILED";
                        throw err;
                    }
                } finally {
                    store._infoPromise = null;
                    store._infoPromiseSid = null;
                }
            })();
            return store._infoPromise;
        },
        async updateBasicInfo(context) {
            try {
                const result = await fetchUserInfo()
                if (result && result.ok) {
                    const data = await result.json();
                    console.log("[user.store updateBasicInfo result]", data);
                    localStorage.setItem('triad_offline_user', JSON.stringify(data));
                    context.commit("INFO", data)
                }
            } catch (e) {
                console.warn("Offline fallback for updateBasicInfo", e);
            }
        },
        iam(context, resource) {
            return verifyIam(context.state.userInfo, resource);
        }
    }
})

export default store;